/**
 * Service for interacting with the signthedoc_ai Python API
 * Handles document signature requests
 */

import axios from 'axios';

const SIGNATURE_API_BASE_URL = import.meta.env.VITE_SIGNATURE_DOC_API_URL || 'http://localhost:8001';

export interface PartyAssignment {
  id: string;
  party_id: string;
  party_name: string;
  party_email: string;
  role_label: string;
  signing_level: number;
  signing_order: number;
  status: string;
  signing_token: string;
  can_sign_now: boolean;
  invited_at: string;
  opened_at: string | null;
  completed_at: string | null;
}

export interface SignatureDocument {
  document_id: string;
  document_type: string;
  owner_name: string;
  docprefix: string;
  created_at: string;
  processed_at: string;
  party_assignments: PartyAssignment[];
}

export interface DocumentType {
  id: number;
  name: string;
  slug: string;
}

export interface SignatureApiResponse {
  status: string;
  party_id: string;
  documents: SignatureDocument[];
}

export interface DocumentStatusResponse {
  status: string;
  data: {
    document_id: string;
    document_status: string;
    total_parties: number;
    completed_parties: number;
    signing_mode: string;
    expires_at: string;
  };
}

/**
 * Fetches all documents for a specific party
 * @param partyId - The party_id of the logged-in party
 * @returns Promise with the list of documents
 */
export async function fetchPartyDocuments(partyId: string): Promise<SignatureApiResponse> {
  try {
    const url = `${SIGNATURE_API_BASE_URL}/api/documents/party/${partyId}/documents`;
    console.log('🌐 Calling Python API:', url);

    const response = await axios.get<SignatureApiResponse>(
      url,
      {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        // Don't use credentials for external API
        withCredentials: false,
        timeout: 10000, // 10 second timeout
      }
    );

    console.log('✅ Signature API response:', response.data);
    console.log('📊 Documents found:', response.data.documents?.length || 0);

    return response.data;
  } catch (error) {
    console.error('❌ Failed to fetch party documents:', error);
    const axiosError = error as any;
    console.error('🔍 Error details:', {
      url: `${SIGNATURE_API_BASE_URL}/api/documents/party/${partyId}/documents`,
      baseUrl: SIGNATURE_API_BASE_URL,
      partyId,
      message: axiosError.message,
      status: axiosError.response?.status,
      data: axiosError.response?.data,
    });
    throw error;
  }
}

/**
 * Gets the signing token for a specific party in a document
 * @param document - The document object
 * @param partyId - The party_id of the party
 * @returns The signing token if found and can_sign_now is true, otherwise null
 */
export function getSigningToken(document: SignatureDocument, partyId: string): string | null {
  const assignment = document.party_assignments?.find(
    (assignment) => assignment.party_id === partyId && assignment.can_sign_now
  );

  return assignment?.signing_token || null;
}

/**
 * Gets the party assignment details for a specific party in a document
 * @param document - The document object
 * @param partyId - The party_id of the party
 * @returns The party assignment if found, otherwise null
 */
export function getPartyAssignment(document: SignatureDocument, partyId: string): PartyAssignment | null {
  return document.party_assignments?.find(
    (assignment) => assignment.party_id === partyId
  ) || null;
}

/**
 * Fetches all document types from the backend
 * @returns Promise with the list of document types
 */
export async function fetchDocumentTypes(): Promise<DocumentType[]> {
  try {
    const url = `${SIGNATURE_API_BASE_URL}/document/types`;
    console.log('🌐 Calling Python API:', url); 
    const response = await axios.get<{ data: DocumentType[] }>(
      url,
      {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      }
    );

    console.log('✅ Document types fetched:', response.data.data);
    return response.data.data || [];
  } catch (error) {
    console.error('❌ Failed to fetch document types:', error);
    return [];
  }
}

/**
 * Gets document type name by ID
 * @param documentTypes - Array of document types
 * @param typeId - The document type ID
 * @returns The document type name or the ID if not found
 */
export function getDocumentTypeName(documentTypes: DocumentType[], typeId: string | number): string {
  if (!typeId) return 'N/A';

  const type = documentTypes.find(dt => dt.id === Number(typeId));
  return type?.name || String(typeId);
}


/**
 * Fetches document status from the signature API
 * @param documentId - The document ID to fetch status for
 * @returns Promise with the document status response
 */
export async function fetchDocumentStatus(documentId: string): Promise<DocumentStatusResponse> {
  try {
    const url = `${SIGNATURE_API_BASE_URL}/api/documents/${documentId}/status`;
    console.log('🌐 Calling document status API:', url);

    const response = await axios.get<DocumentStatusResponse>(
      url,
      {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        withCredentials: false,
        timeout: 10000, // 10 second timeout
      }
    );

    console.log('✅ Document status fetched:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Failed to fetch document status:', error);
    const axiosError = error as any;
    console.error('🔍 Error details:', {
      url: `${SIGNATURE_API_BASE_URL}/api/documents/${documentId}/status`,
      message: axiosError.message,
      status: axiosError.response?.status,
      data: axiosError.response?.data,
    });
    throw error;
  }
}

/**
 * Gets status display text based on document status
 * @param status - The document status from API
 * @returns Formatted status text for display
 */
export function getStatusDisplayText(status: string): string {
  switch (status?.toLowerCase()) {
    case 'in_progress':
      return 'In Progress';
    case 'completed':
      return 'Completed';
    case 'pending':
      return 'Pending';
    case 'expired':
      return 'Expired';
    case 'cancelled':
      return 'Cancelled';
    default:
      return status || 'Unknown';
  }
}

/**
 * Gets status badge color based on document status
 * @param status - The document status from API
 * @returns Tailwind CSS classes for status badge
 */
export function getStatusBadgeClasses(status: string): string {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'bg-green-100 text-green-800 border border-green-200';
    case 'in_progress':
      return 'bg-blue-100 text-blue-800 border border-blue-200';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
    case 'expired':
      return 'bg-red-100 text-red-800 border border-red-200';
    case 'cancelled':
      return 'bg-gray-100 text-gray-800 border border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 border border-gray-200';
  }
}

/**
 * Fetches document type by disk_prefix from digital_wallets/file_entries tables
 * @param diskPrefix - The disk_prefix to search for
 * @returns Promise with the document type name
 */
export async function fetchDocumentTypeByDiskPrefix(diskPrefix: string): Promise<string | null> {
  console.log('🚀 API: Starting fetch for disk_prefix:', diskPrefix);

  try {
    const url = `/api/v1/digital-wallets/by-disk-prefix/${encodeURIComponent(diskPrefix)}`;
    console.log('📡 API: Making request to:', url);

    const response = await axios.get(url);
    console.log('📥 API: Got response with status:', response.status);
    console.log('📦 API: Response data type:', typeof response.data);

    if (typeof response.data === 'string') {
      console.log('📄 API: Response is string, first 200 chars:', response.data.substring(0, 200));
      console.log('📄 API: Full response string:', response.data);
      return null;
    }

    if (response.data && typeof response.data === 'object') {
      console.log('📦 API: Full response object:', response.data);
      if (response.data.document_type_name) {
        console.log('✅ API: Found document_type_name:', response.data.document_type_name);
        return response.data.document_type_name;
      } else {
        console.log('⚠️ API: Response object but no document_type_name field');
        console.log('🔍 API: Available fields:', Object.keys(response.data));
      }
    } else {
      console.log('⚠️ API: Response is not an object or string:', typeof response.data);
    }

    return null;
  } catch (error: any) {
    console.error('💥 API: Exception occurred for disk_prefix:', diskPrefix);
    console.error('💥 API: Error message:', error.message);

    if (error.response) {
      console.error('💥 API: Response status:', error.response.status);
      console.error('💥 API: Response headers:', error.response.headers);
      console.error('💥 API: Response data:', error.response.data);
    } else if (error.request) {
      console.error('💥 API: No response received, request details:', error.request);
    } else {
      console.error('💥 API: Error setting up request:', error.message);
    }

    return null;
  }
}

